import { CraftConfiguration } from './craft.model';
import { TransportConfiguration } from './transport.model';
import { LocalizationLanguage } from './localization.model';
import { LOCALIZATION_LANGUAGES } from '../constants/localization.constant';
import { PriceFavoritesList } from './price-favorites.model';
import { getAllRoyalMarketLocations } from '../constants/locations.constant';
import { Location } from './location.model';
import { PriceConfiguration } from './price.model';
import { FlipperConfiguration as FlipperConfiguration, MarketFlip } from './flipper.model';
import { FarmConfiguration } from './farming-models/farm-models';
import { Timestamp, QueryDocumentSnapshot, DocumentData } from 'firebase/firestore/lite';
import { PathfinderConfiguration } from './pathfinder.models';

export class AfmUser {
  uId: string;
  creationDate: Date;
  afmUserSettings: AfmUserSettings;
  name?: string;
  email?: string;
  photoUrl?: string;
  userSpecMap: Map<string, number> = new Map<string, number>();
  priceFavoritesLists: PriceFavoritesList[] = [];
  //this is not stored in the database, it is used to keep track of the flips consumed by the user
  consumedFlips: MarketFlip[] = [];

  constructor(
    _uId: string,
    _creationDate: Date,
    _afmUserSettings?: AfmUserSettings,
    _name?: string,
    _email?: string,
    _photoUrl?: string,
    _userSpecMap?: Map<string, number>,
    _priceFavoritesLists?: PriceFavoritesList[]
  ) {
    this.uId = _uId;
    this.creationDate = _creationDate;
    this.afmUserSettings = _afmUserSettings ?? new AfmUserSettings(_uId);
    this.name = _name;
    this.email = _email;
    this.photoUrl = _photoUrl;
    if (_userSpecMap) {
      this.userSpecMap = _userSpecMap;
    }
    if (_priceFavoritesLists) {
      this.priceFavoritesLists = _priceFavoritesLists;
    }
  }
}

export const afmUserConverter = {
  toFirestore: (user: AfmUser) => {
    return {
      uId: user.uId,
      creationDate: Timestamp.fromDate(user.creationDate),
      afmUserSettings: JSON.parse(JSON.stringify(user.afmUserSettings)),
      name: user.name,
      email: user.email,
      photoUrl: user.photoUrl,
      userSpecMap: Object.fromEntries(user.userSpecMap ?? new Map<string, number>()),
      priceFavoritesLists: JSON.parse(JSON.stringify(user.priceFavoritesLists)),
    };
  },
  fromFirestore: (snapshot: QueryDocumentSnapshot<DocumentData>) => {
    const data = snapshot.data() as AfmUser;
    const creationDate = data.creationDate;
    let settings = new AfmUserSettings(
      data.uId,
      data.afmUserSettings.server,
      data.afmUserSettings.theme,
      LocalizationLanguage.fromJson(JSON.stringify(data.afmUserSettings.language)),
      CraftConfiguration.fromJson(JSON.stringify(data.afmUserSettings.craftingConfiguration ?? new CraftConfiguration())),
      TransportConfiguration.fromJson(JSON.stringify(data.afmUserSettings.transportConfiguration ?? new TransportConfiguration())),
      PriceConfiguration.fromJson(JSON.stringify(data.afmUserSettings.priceConfiguration ?? new PriceConfiguration())),
      PathfinderConfiguration.fromJson(JSON.stringify(data.afmUserSettings.pathfinderConfiguration ?? new PathfinderConfiguration())),
      FlipperConfiguration.fromJson(JSON.stringify(data.afmUserSettings.flipperConfiguration ?? new FlipperConfiguration())),
      FarmConfiguration.fromJson(JSON.stringify(data.afmUserSettings.farmConfiguration ?? new FarmConfiguration())),
      data.afmUserSettings.selectedCities?.map((location: Location) => Location.fromJson(JSON.stringify(location))) ?? getAllRoyalMarketLocations()
    );
    const userSpecMap = new Map<string, number>(Object.entries(data.userSpecMap || {}));
    const favoritesLists = JSON.parse(JSON.stringify(data.priceFavoritesLists));
    return new AfmUser(data.uId, creationDate, settings, data.name, data.email, data.photoUrl, userSpecMap, favoritesLists);
  },
};

export class AfmUserSettings {
  uId: string;
  server: string;
  theme: string;
  language: LocalizationLanguage;
  craftingConfiguration: CraftConfiguration;
  transportConfiguration: TransportConfiguration;
  priceConfiguration: PriceConfiguration;
  flipperConfiguration: FlipperConfiguration;
  farmConfiguration: FarmConfiguration;
  pathfinderConfiguration: PathfinderConfiguration;
  selectedCities: Location[] = getAllRoyalMarketLocations();

  constructor(
    _uId: string,
    _server?: string,
    _theme?: string,
    _language?: LocalizationLanguage,
    _craftingConfiguration?: CraftConfiguration,
    _transportConfiguration?: TransportConfiguration,
    _priceConfiguration?: PriceConfiguration,
    _pathfinderConfiguration?: PathfinderConfiguration,
    _flipperConfiguration?: FlipperConfiguration,
    _farmConfiguration?: FarmConfiguration,
    _selectedCities?: Location[]
  ) {
    this.uId = _uId;
    this.server = _server ?? 'west';
    this.theme = _theme ?? 'albion-dark-theme';
    this.language = _language ?? LOCALIZATION_LANGUAGES.EN_US;
    this.craftingConfiguration = _craftingConfiguration ?? new CraftConfiguration();
    this.transportConfiguration = _transportConfiguration ?? new TransportConfiguration();
    this.priceConfiguration = _priceConfiguration ?? new PriceConfiguration();
    this.pathfinderConfiguration = _pathfinderConfiguration ?? new PathfinderConfiguration();
    this.flipperConfiguration = _flipperConfiguration ?? new FlipperConfiguration();
    this.farmConfiguration = _farmConfiguration ?? new FarmConfiguration();
    if (_selectedCities) {
      this.selectedCities = _selectedCities;
    }
  }
}
